import { useEffect, useState } from "react";

/**
 * Pip dice used by the Recovery Board and its homepage card. The face is drawn
 * from a 3×3 grid so it always matches the theme (gold pips on a card surface)
 * and reads clearly at small sizes; `rolling` adds the tumble animation.
 */
const PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const FACE = {
  xs: "h-7 w-7 gap-[2px] p-[4px] rounded-[6px]",
  sm: "h-9 w-9 gap-[3px] p-[5px] rounded-[8px]",
  md: "h-11 w-11 gap-[4px] p-[6px] rounded-[10px]",
  lg: "h-14 w-14 gap-[5px] p-[8px] rounded-xl",
};

const DOT = {
  xs: "h-[5px] w-[5px]",
  sm: "h-[6px] w-[6px]",
  md: "h-2 w-2",
  lg: "h-[9px] w-[9px]",
};

export function Die({
  value,
  size = "md",
  rolling = false,
  className = "",
}: {
  value: number;
  size?: keyof typeof FACE;
  rolling?: boolean;
  className?: string;
}) {
  const pips = PIPS[value] ?? [];
  return (
    <span
      aria-hidden
      className={`grid shrink-0 grid-cols-3 grid-rows-3 place-items-center border-2 border-[var(--gold-deep)] bg-card shadow-sm ${FACE[size]} ${rolling ? "rt-dice-tumble" : ""} ${className}`}
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <span
          key={i}
          className={`${DOT[size]} rounded-full ${pips.includes(i) ? "bg-[var(--gold-deep)]" : "bg-transparent"}`}
        />
      ))}
    </span>
  );
}

/**
 * Two decorative dice that tumble and settle, then roll again after a rest.
 * Stays completely still when the visitor asks for less movement.
 */
export function RollingDice({
  size = "sm",
  className = "",
}: {
  size?: keyof typeof FACE;
  className?: string;
}) {
  const [values, setValues] = useState<number[]>([3, 5]);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let spin = 0;
    let rest = 0;
    const roll = () => {
      setRolling(true);
      let ticks = 0;
      spin = window.setInterval(() => {
        setValues([1 + Math.floor(Math.random() * 6), 1 + Math.floor(Math.random() * 6)]);
        if (++ticks >= 9) {
          window.clearInterval(spin);
          setRolling(false);
          rest = window.setTimeout(roll, 3600);
        }
      }, 110);
    };
    rest = window.setTimeout(roll, 700);
    return () => {
      window.clearInterval(spin);
      window.clearTimeout(rest);
    };
  }, []);

  return (
    <span className={`flex items-center justify-center gap-1.5 ${className}`}>
      <Die value={values[0]} size={size} rolling={rolling} />
      <Die value={values[1]} size={size} rolling={rolling} className="rt-dice-tilt" />
    </span>
  );
}
